import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/api';
import '../styles/admin-dashboard.css';

function AdminEventPublishPlansManagement() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    weeks: 1,
    durationDays: 7,
    price: 499,
    currency: 'INR',
    badge: 'Starter',
    displayOrder: 1,
    isActive: true,
  });

  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, plan: null, loading: false });

  // Notification Toast / Alert
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4000);
  };

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApi.getEventPublishPlans({ all: 'true' });
      if (res?.success || res?.plans || Array.isArray(res)) {
        const list = res.plans || res.data || (Array.isArray(res) ? res : []);
        setPlans(list);
      }
    } catch (err) {
      console.error('Error fetching event publish plans:', err);
      showFeedback('error', err.message || 'Failed to load event publish plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Derived Dynamic Real-Time Statistics
  const totalPlans = plans.length;
  const activePlans = plans.filter((p) => p.isActive !== false).length;
  const inactivePlans = plans.filter((p) => p.isActive === false).length;
  const averagePrice =
    totalPlans > 0
      ? Math.round(plans.reduce((sum, p) => sum + (Number(p.price) || 0), 0) / totalPlans)
      : 0;

  // Filtered plans list
  const filteredPlans = plans.filter((plan) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !q ||
      (plan.name && plan.name.toLowerCase().includes(q)) ||
      (plan.badge && plan.badge.toLowerCase().includes(q)) ||
      String(plan.weeks).includes(q) ||
      String(plan.price).includes(q);

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && plan.isActive !== false) ||
      (statusFilter === 'inactive' && plan.isActive === false);

    return matchesSearch && matchesStatus;
  });

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setEditingId(null);
    const nextWeeks = plans.length + 1;
    setFormData({
      name: `${nextWeeks} Week${nextWeeks > 1 ? 's' : ''} Package`,
      weeks: nextWeeks,
      durationDays: nextWeeks * 7,
      price: nextWeeks * 450,
      currency: 'INR',
      badge: nextWeeks === 1 ? 'Starter' : nextWeeks === 2 ? 'Popular' : 'Featured',
      displayOrder: plans.length + 1,
      isActive: true,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (plan) => {
    const id = plan._id || plan.id;
    setModalMode('edit');
    setEditingId(id);
    setFormData({
      name: plan.name || '',
      weeks: plan.weeks || 1,
      durationDays: plan.durationDays || (plan.weeks || 1) * 7,
      price: plan.price ?? 0,
      currency: plan.currency || 'INR',
      badge: plan.badge || '',
      displayOrder: plan.displayOrder ?? 0,
      isActive: plan.isActive !== undefined ? plan.isActive : true,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  // Form Submit
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setFormError('Plan title is required');
      return;
    }

    const weeksNum = Math.max(1, parseInt(formData.weeks, 10) || 1);
    const durationDaysNum = parseInt(formData.durationDays, 10) || weeksNum * 7;
    const priceNum = Math.max(0, parseFloat(formData.price) || 0);

    const payload = {
      name: formData.name.trim(),
      weeks: weeksNum,
      durationDays: durationDaysNum,
      price: priceNum,
      currency: (formData.currency || 'INR').toUpperCase(),
      badge: formData.badge ? formData.badge.trim() : '',
      displayOrder: parseInt(formData.displayOrder, 10) || 0,
      isActive: Boolean(formData.isActive),
    };

    try {
      setFormLoading(true);
      setFormError('');

      if (modalMode === 'create') {
        const res = await adminApi.createEventPublishPlan(payload);
        showFeedback('success', `Plan "${payload.name}" created successfully!`);
        setIsModalOpen(false);
        if (res?.plan) {
          setPlans((prev) => [...prev, res.plan]);
        }
        fetchPlans();
      } else {
        const planId = editingId;
        // Optimistic update
        setPlans((prev) =>
          prev.map((p) => ((p._id === planId || p.id === planId) ? { ...p, ...payload } : p))
        );

        const res = await adminApi.updateEventPublishPlan(planId, payload);
        showFeedback('success', `Plan "${payload.name}" updated successfully!`);
        setIsModalOpen(false);
        if (res?.plan) {
          setPlans((prev) =>
            prev.map((p) => ((p._id === planId || p.id === planId) ? res.plan : p))
          );
        }
        fetchPlans();
      }
    } catch (err) {
      console.error('Error saving publish plan:', err);
      setFormError(err.message || 'Failed to save publish plan');
      fetchPlans();
    } finally {
      setFormLoading(false);
    }
  };

  // Toggle Active Status
  const handleToggleStatus = async (plan) => {
    const planId = plan._id || plan.id;
    const nextStatus = !(plan.isActive !== false);

    // Optimistic update
    setPlans((prev) =>
      prev.map((p) => ((p._id === planId || p.id === planId) ? { ...p, isActive: nextStatus } : p))
    );

    try {
      const res = await adminApi.toggleEventPublishPlan(planId);
      const finalStatus = res?.plan ? res.plan.isActive : nextStatus;
      showFeedback('success', `Plan "${plan.name}" is now ${finalStatus ? 'Active' : 'Inactive'}`);
      if (res?.plan) {
        setPlans((prev) =>
          prev.map((p) => ((p._id === planId || p.id === planId) ? res.plan : p))
        );
      }
    } catch (err) {
      console.error('Error toggling plan status:', err);
      showFeedback('error', err.message || 'Failed to toggle status');
      fetchPlans();
    }
  };

  // Delete Plan
  const handleDeleteConfirm = async () => {
    if (!deleteModal.plan) return;
    const planId = deleteModal.plan._id || deleteModal.plan.id;
    const planName = deleteModal.plan.name;

    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));
      // Optimistic delete
      setPlans((prev) => prev.filter((p) => p._id !== planId && p.id !== planId));

      await adminApi.deleteEventPublishPlan(planId);
      showFeedback('success', `Publish plan "${planName}" deleted successfully`);
      setDeleteModal({ isOpen: false, plan: null, loading: false });
      fetchPlans();
    } catch (err) {
      console.error('Error deleting publish plan:', err);
      showFeedback('error', err.message || 'Failed to delete publish plan');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
      fetchPlans();
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      {/* Top Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '28px',
          flexWrap: 'wrap',
          gap: '15px',
        }}
      >
        <div>
          <h2
            style={{
              margin: '0 0 6px 0',
              fontSize: '1.8rem',
              fontWeight: 800,
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            🗓️ Event Publish Weeks & Pricing Plans
          </h2>
          <p style={{ margin: 0, color: '#64748b', fontSize: '0.95rem' }}>
            Set up listing durations (1 week, 2 weeks, 1 month, etc.) and prices for therapists publishing events.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={fetchPlans}
            style={{
              padding: '10px 18px',
              background: '#ffffff',
              color: '#475569',
              border: '1.5px solid #cbd5e1',
              borderRadius: '10px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.9rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            🔄 Refresh
          </button>

          <button
            type="button"
            onClick={handleOpenCreateModal}
            style={{
              padding: '10px 22px',
              background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: '0.95rem',
              boxShadow: '0 4px 14px rgba(124, 58, 237, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>➕</span> Add Publish Plan / Week Tier
          </button>
        </div>
      </div>

      {/* Alert Banner */}
      {feedback.message && (
        <div
          style={{
            padding: '14px 20px',
            borderRadius: '12px',
            marginBottom: '24px',
            fontWeight: 600,
            fontSize: '0.92rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: feedback.type === 'success' ? '#ecfdf5' : '#fef2f2',
            color: feedback.type === 'success' ? '#065f46' : '#991b1b',
            border: `1.5px solid ${feedback.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
          }}
        >
          <span>{feedback.type === 'success' ? '✅' : '⚠️'} {feedback.message}</span>
          <button
            onClick={() => setFeedback({ type: '', message: '' })}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 'bold' }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Metric Cards (Real-time Dynamic Counters) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: '20px',
          marginBottom: '28px',
        }}
      >
        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <div style={{ color: '#64748b', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '6px' }}>
            TOTAL PRICING TIERS
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#1e293b' }}>{totalPlans}</div>
        </div>

        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <div style={{ color: '#059669', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '6px' }}>
            ACTIVE TIERS
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#059669' }}>{activePlans}</div>
        </div>

        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <div style={{ color: '#7c3aed', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '6px' }}>
            AVERAGE PACKAGE PRICE
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#7c3aed' }}>₹{averagePrice}</div>
        </div>

        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <div style={{ color: '#475569', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '6px' }}>
            INACTIVE TIERS
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#475569' }}>{inactivePlans}</div>
        </div>
      </div>

      {/* Search & Filter Toolbar */}
      <div
        style={{
          background: '#ffffff',
          padding: '16px 22px',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div style={{ position: 'relative', width: '100%', maxWidth: '320px' }}>
          <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>🔍</span>
          <input
            type="text"
            placeholder="Search plan, weeks, price..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '9px 14px 9px 38px',
              borderRadius: '8px',
              border: '1.5px solid #cbd5e1',
              fontSize: '0.88rem',
              outline: 'none',
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f8fafc', padding: '4px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', paddingLeft: '8px', paddingRight: '4px' }}>Status:</span>
          {['all', 'active', 'inactive'].map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(status)}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: 'none',
                background: statusFilter === status ? '#7c3aed' : 'transparent',
                color: statusFilter === status ? '#ffffff' : '#64748b',
                fontSize: '0.82rem',
                fontWeight: 600,
                cursor: 'pointer',
                textTransform: 'capitalize',
                transition: 'all 0.2s',
              }}
            >
              {status === 'all' ? 'All' : status}
            </button>
          ))}
        </div>
      </div>

      {/* Plans Table */}
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0,0,0,0.02)',
        }}
      >
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '2.5rem', height: '2.5rem', color: '#7c3aed' }}></div>
            <p style={{ marginTop: '14px', color: '#64748b', fontWeight: 600 }}>Loading publish plans...</p>
          </div>
        ) : filteredPlans.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', color: '#475569', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <th style={{ padding: '16px 20px' }}>Plan Name & Badge</th>
                  <th style={{ padding: '16px 20px' }}>Publish Duration</th>
                  <th style={{ padding: '16px 20px' }}>Price</th>
                  <th style={{ padding: '16px 20px', textAlign: 'center' }}>Display Order</th>
                  <th style={{ padding: '16px 20px', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '16px 20px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlans.map((plan) => {
                  const planId = plan._id || plan.id;
                  const isPlanActive = plan.isActive !== false;

                  return (
                    <tr key={planId} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}>
                      {/* Name & Badge */}
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                            {plan.name}
                          </span>
                          {plan.badge && (
                            <span
                              style={{
                                padding: '2px 8px',
                                background: '#f5f3ff',
                                color: '#7c3aed',
                                border: '1px solid #ddd6fe',
                                borderRadius: '12px',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                              }}
                            >
                              ★ {plan.badge}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Duration */}
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f1f5f9', padding: '4px 10px', borderRadius: '8px', fontWeight: 700, color: '#334155', fontSize: '0.88rem' }}>
                          <span>🗓️ {plan.weeks} {plan.weeks === 1 ? 'Week' : 'Weeks'}</span>
                          <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>({plan.durationDays || plan.weeks * 7} Days)</span>
                        </div>
                      </td>

                      {/* Price */}
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#059669' }}>
                          {plan.currency === 'USD' ? '$' : '₹'}{plan.price}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          per event listing
                        </div>
                      </td>

                      {/* Display Order */}
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <span style={{ fontWeight: 700, color: '#334155', fontSize: '0.88rem', background: '#f8fafc', padding: '3px 8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                          {plan.displayOrder ?? 0}
                        </span>
                      </td>

                      {/* Status */}
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(plan)}
                          style={{
                            padding: '5px 12px',
                            borderRadius: '20px',
                            border: `1px solid ${isPlanActive ? '#a7f3d0' : '#cbd5e1'}`,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            background: isPlanActive ? '#ecfdf5' : '#f1f5f9',
                            color: isPlanActive ? '#065f46' : '#64748b',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            transition: 'all 0.2s',
                          }}
                          title="Click to toggle status"
                        >
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: isPlanActive ? '#10b981' : '#94a3b8' }}></span>
                          {isPlanActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(plan)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '8px',
                              border: '1.5px solid #cbd5e1',
                              background: '#ffffff',
                              color: '#334155',
                              fontSize: '0.82rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <span>✏️</span> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteModal({ isOpen: true, plan, loading: false })}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '8px',
                              border: '1.5px solid #fecaca',
                              background: '#fff5f5',
                              color: '#dc2626',
                              fontSize: '0.82rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <span>🗑️</span> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '14px' }}>🗓️</div>
            <h4 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>No pricing plans found</h4>
            <p style={{ color: '#64748b', margin: '0 auto 16px auto', maxWidth: '420px', fontSize: '0.92rem' }}>
              Configure your first publishing duration package (e.g. 1 Week for ₹499).
            </p>
            <button
              type="button"
              onClick={handleOpenCreateModal}
              style={{
                padding: '10px 22px',
                borderRadius: '10px',
                border: 'none',
                background: '#7c3aed',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              ➕ Create Week Plan
            </button>
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* CREATE / EDIT PLAN MODAL */}
      {/* ========================================================================= */}
      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(5px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => !formLoading && setIsModalOpen(false)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              width: '100%',
              maxWidth: '560px',
              maxHeight: '92vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ padding: '24px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>
                  {modalMode === 'create' ? '➕ Create Event Publish Plan' : '✏️ Edit Publish Plan'}
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                  Define duration in weeks/days, listing price, and badges for event publishing
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1.1rem', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ✕
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleFormSubmit} style={{ padding: '24px 28px' }}>
              {formError && (
                <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#dc2626', fontSize: '0.88rem', marginBottom: '18px', fontWeight: 600 }}>
                  ⚠️ {formError}
                </div>
              )}

              {/* Plan Title & Badge */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Plan Title <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. 1 Week Showcase, 2 Weeks Spotlight"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.92rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Badge / Tag
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Starter, Popular"
                    value={formData.badge}
                    onChange={(e) => setFormData({ ...formData, badge: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.92rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              {/* Weeks, Duration Days, and Price */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', gap: '14px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Duration (Weeks) <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={formData.weeks}
                    onChange={(e) => {
                      const w = parseInt(e.target.value) || 1;
                      setFormData({ ...formData, weeks: w, durationDays: w * 7 });
                    }}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.92rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Total Days
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.durationDays}
                    onChange={(e) => setFormData({ ...formData, durationDays: parseInt(e.target.value) || 7 })}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.92rem',
                      outline: 'none',
                      background: '#f8fafc',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Price ({formData.currency}) <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontWeight: 700, color: '#059669' }}>
                      {formData.currency === 'USD' ? '$' : '₹'}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      required
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                      style={{
                        width: '100%',
                        padding: '11px 14px 11px 30px',
                        borderRadius: '10px',
                        border: '1.5px solid #cbd5e1',
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Display Order & Active Checkbox */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.9rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', paddingTop: '24px' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.92rem', fontWeight: 600, color: '#1e293b' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    Active / Available to Therapists
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={formLoading}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#475569',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
                  }}
                >
                  {formLoading ? 'Saving...' : modalMode === 'create' ? 'Create Plan' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* DELETE CONFIRMATION MODAL */}
      {/* ========================================================================= */}
      {deleteModal.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(5px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => !deleteModal.loading && setDeleteModal({ isOpen: false, plan: null, loading: false })}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              width: '100%',
              maxWidth: '440px',
              padding: '28px',
              textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🗑️</div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', marginBottom: '8px' }}>
              Delete Publish Plan?
            </h3>
            <p style={{ color: '#64748b', fontSize: '0.92rem', lineHeight: 1.5, marginBottom: '24px' }}>
              Are you sure you want to delete <strong>"{deleteModal.plan?.name}"</strong> ({deleteModal.plan?.weeks} Week(s) @ ₹{deleteModal.plan?.price})?
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setDeleteModal({ isOpen: false, plan: null, loading: false })}
                disabled={deleteModal.loading}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: '1.5px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#475569',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleteModal.loading}
                style={{
                  padding: '10px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#dc2626',
                  color: '#ffffff',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)',
                }}
              >
                {deleteModal.loading ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminEventPublishPlansManagement;
